import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  useCreateCooperativeMutation,
  useGetSubscriptionsQuery,
  useCheckDomainAvailabilityMutation,
  useCheckEmailAvailabilityMutation,
} from "../../store/auth/authApi";
import {
  useGetCountriesQuery,
  useGetCitiesQuery,
} from "../../store/selectableSlice";
import { uploadImage } from "../../utils/uploadImage";
import { Input } from "../../components/Ui/new-input";
import CustomSelect from "../../components/custom-select/custom-select";
import { customStyles } from "../../components/custom-select/custom-styles";
import { FileUploaderComp } from "../../components/Ui/FileUploader";
import ThemeBrandingEditor from "../../components/Common/ThemeBrandingEditor";
import { DEFAULT_THEME, themeToApiPayload } from "../../theme/companyTheme";

const hexColorRegExp = /^#[0-9A-Fa-f]{6}$/;

const CreateCooperativePage = () => {
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [domainStatus, setDomainStatus] = useState(null); // null, 'checking', 'available', 'taken'
  const [emailStatus, setEmailStatus] = useState(null); // null, 'checking', 'available', 'taken'
  const navigate = useNavigate();

  const [createCooperative, { isLoading: isCreating }] =
    useCreateCooperativeMutation();
  const [checkDomain] = useCheckDomainAvailabilityMutation();
  const [checkEmail] = useCheckEmailAvailabilityMutation();
  const { data: countries = [], isLoading: isLoadingCountries } =
    useGetCountriesQuery();
  const { data: cities = [], isLoading: isLoadingCities } = useGetCitiesQuery();
  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } =
    useGetSubscriptionsQuery();

  // Extract subscriptions array from the response
  const subscriptions = subscriptionsData?.data || [];

  // Domain validation function
  const validateDomain = (value) => {
    if (!value) return true; // Let required validation handle empty values
    const subdomainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
    return subdomainRegex.test(value);
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      country_id: "",
      city_id: "",
      address: "",
      domain: "",
      subscription_id: "",
      member_slot_count: 1,
      discount_percentage: "",
      subscription_start_date: "",
      subscription_duration: "",
      subscription_duration_type: "MONTHS",
      primary_color: DEFAULT_THEME.primary,
      secondary_color: DEFAULT_THEME.secondary,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Cooperative name is required"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      phone: Yup.string()
        .required("Phone number is required")
        .matches(
          /^[0-9]{10}$/,
          "Phone number must be 10 digits (excluding country code)"
        ),
      country_id: Yup.string().required("Country is required"),
      city_id: Yup.string().required("City is required"),
      address: Yup.string().required("Address is required"),
      domain: Yup.string()
        .required("Domain is required")
        .test(
          "domain-format",
          "Domain can only contain lowercase letters, numbers, and hyphens",
          validateDomain
        )
        .min(3, "Domain must be at least 3 characters")
        .max(50, "Domain must be less than 50 characters"),
      subscription_id: Yup.string().required("Subscription plan is required"),
      member_slot_count: Yup.number()
        .required("Number of member slots is required")
        .min(1, "At least 1 slot is required")
        .integer("Must be a whole number"),
      discount_percentage: Yup.number()
        .nullable()
        .min(0, "Discount must be between 0 and 100")
        .max(100, "Discount must be between 0 and 100"),
      subscription_start_date: Yup.date()
        .required("Subscription start date is required")
        .min(new Date().toISOString().split("T")[0], "Start date cannot be in the past"),
      subscription_duration: Yup.number()
        .required("Subscription duration is required")
        .min(1, "Duration must be at least 1"),
      subscription_duration_type: Yup.string()
        .required("Duration type is required")
        .oneOf(["DAYS", "WEEKS", "MONTHS", "YEARS"], "Invalid duration type"),
      primary_color: Yup.string().required("Primary color is required").matches(hexColorRegExp, "Enter a valid hex color"),
      secondary_color: Yup.string().required("Secondary color is required").matches(hexColorRegExp, "Enter a valid hex color"),
    }),
    onSubmit: async (values) => {
      try {
        // Format phone number with country code
        const formattedPhone = `0${values.phone}`; // Add leading zero for Nigerian format

        // Remove subscription_expiry_date if it exists (we're using start_date + duration now)
        const { subscription_expiry_date, ...restValues } = values;

        const payload = {
          ...restValues,
          phone: formattedPhone,
          logo_url:
            logoUrl ||
            "https://www.gravatar.com/avatar/a258389389f40731d3ac10839ec143a2?d=identicon&r=pg&s=100",
          country_id: parseInt(values.country_id),
          city_id: parseInt(values.city_id),
          subscription_id: parseInt(values.subscription_id),
          member_slot_count: parseInt(values.member_slot_count, 10) || 1,
          discount_percentage: values.discount_percentage !== "" && values.discount_percentage != null
            ? parseFloat(values.discount_percentage)
            : 0,
          ...themeToApiPayload({
            primary: values.primary_color,
            secondary: values.secondary_color,
          }),
        };

        const result = await createCooperative(payload).unwrap();

        if (result.status) {
          toast.success("Cooperative created successfully!");
          navigate("/cooperatives");
        } else {
          toast.error(result.message || "Failed to create cooperative");
        }
      } catch (error) {
        console.error("Error creating cooperative:", error);
        const status = error?.status ?? error?.data?.status;
        const backendMessage = error?.data?.message || error?.error?.message || error?.message || "Failed to create cooperative";
        let errorMessage = backendMessage;
        if (status === 401) {
          errorMessage = `Authorization failed (401). Log in again on this environment — token may be from a different server. ${backendMessage}`;
        } else if (status === 403) {
          errorMessage = `Access denied (403). ${backendMessage}`;
        }
        toast.error(errorMessage);
      }
    },
  });

  const handleLogoUpload = async (file) => {
    if (!file) return;

    // Check if file is a string (URL) or File object
    if (typeof file === "string") {
      // If it's already a URL, just set it
      setLogoUrl(file);
      return;
    }

    // If it's a File object, create preview and upload
    if (!(file instanceof Blob || file instanceof File)) {
      console.error("Invalid file type:", file);
      toast.error("Invalid file type");
      return;
    }

    setIsUploading(true);
    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);

      // Upload to Cloudinary
      const uploadedUrl = await uploadImage(file);
      setLogoUrl(uploadedUrl);
      toast.success("Logo uploaded successfully!");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Failed to upload logo");
      // Clean up preview URL on error
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
        setLogoPreview("");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    // Clean up object URL to prevent memory leaks
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoUrl("");
    setLogoPreview("");
  };

  // Cleanup object URL when component unmounts
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  // Debounced domain availability check
  useEffect(() => {
    const domain = formik.values.domain?.trim();
    
    if (!domain || domain.length < 3) {
      setDomainStatus(null);
      return;
    }

    // Validate format first
    if (!validateDomain(domain)) {
      setDomainStatus(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setDomainStatus('checking');
      try {
        const result = await checkDomain(domain).unwrap();
        setDomainStatus(result.data?.available ? 'available' : 'taken');
      } catch (error) {
        setDomainStatus('taken'); // Assume taken on error
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formik.values.domain, checkDomain]);

  // Debounced email availability check
  useEffect(() => {
    const email = formik.values.email?.trim();
    
    if (!email || !email.includes('@')) {
      setEmailStatus(null);
      return;
    }

    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailStatus(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setEmailStatus('checking');
      try {
        const result = await checkEmail(email).unwrap();
        setEmailStatus(result.data?.available ? 'available' : 'taken');
      } catch (error) {
        setEmailStatus('taken'); // Assume taken on error
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formik.values.email, checkEmail]);

  const handleSubscriptionChange = (selectedOption) => {
    formik.setFieldValue("subscription_id", selectedOption?.value || "");
    const subscription = Array.isArray(subscriptions)
      ? subscriptions.find((sub) => sub.id === selectedOption?.value)
      : null;
    setSelectedSubscription(subscription);
  };

  // Filter cities based on selected country
  const filteredCities = cities.filter(
    (city) =>
      !formik.values.country_id ||
      city.country_id === parseInt(formik.values.country_id)
  );

  // Prepare options for selects
  const countryOptions = countries.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  const cityOptions = filteredCities.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  const subscriptionOptions = Array.isArray(subscriptions)
    ? subscriptions.map((subscription) => ({
        value: subscription.id,
        label: subscription.name,
        data: subscription,
      }))
    : [];

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="text-2xl font-bold text-[#45464e] mb-0">
            Create New Cooperative
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
        <form onSubmit={formik.handleSubmit}>
          <div className="row">
            {/* Logo Upload Section */}
            <div className="col-12 mb-6">
              <div className="d-flex align-items-center gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex-shrink-0">
                  {logoPreview ? (
                    <div className="position-relative">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="rounded-lg shadow-sm"
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "cover",
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute rounded-full"
                        style={{
                          top: "-8px",
                          right: "-8px",
                          width: "24px",
                          height: "24px",
                          padding: "0",
                          fontSize: "12px",
                        }}
                        onClick={handleRemoveLogo}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg d-flex align-items-center justify-content-center bg-white"
                      style={{ width: "80px", height: "80px" }}
                    >
                      <svg
                        className="w-8 h-8 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-grow-1">
                  <h6 className="text-gray-700 font-medium mb-2">
                    Cooperative Logo
                  </h6>
                  <FileUploaderComp
                    LabelText=""
                    bodyText="Click to upload or drag and drop your logo here"
                    onChange={handleLogoUpload}
                    required={false}
                    acceptImages={true}
                  />
                  {isUploading && (
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <svg
                        className="animate-spin h-4 w-4 text-[#8B313A]"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <small className="text-[#8B313A] font-medium">
                        Uploading...
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="col-md-6 mb-3">
              <Input
                label="Cooperative Name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && formik.errors.name}
                errorMessage={formik.errors.name}
                required
                placeholder="Enter cooperative name"
              />
            </div>

            <div className="col-md-6 mb-3">
              <Input
                label="Email Address"
                name="email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && formik.errors.email}
                errorMessage={formik.errors.email}
                required
                placeholder="Enter email address"
              />
              {emailStatus === 'checking' && (
                <small className="text-info d-flex align-items-center gap-1 mt-1">
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Checking availability...
                </small>
              )}
              {emailStatus === 'available' && (
                <small className="text-success d-flex align-items-center gap-1 mt-1">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Email is available
                </small>
              )}
              {emailStatus === 'taken' && (
                <small className="text-danger d-flex align-items-center gap-1 mt-1">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Email is already taken
                </small>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <Input
                label="Phone Number"
                name="phone"
                type="tel"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && formik.errors.phone}
                errorMessage={formik.errors.phone}
                required
                placeholder="Enter 10-digit phone number"
              />
              <small className="text-muted">
                Enter 10 digits (e.g., 7065873900). Country code will be added
                automatically.
              </small>
            </div>

            <div className="col-md-6 mb-3">
              <Input
                label="Domain"
                name="domain"
                value={formik.values.domain}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.domain && formik.errors.domain}
                errorMessage={formik.errors.domain}
                required
                placeholder="e.g., mycoop"
              />
              <small className="text-muted d-block">
                Only lowercase letters, numbers, and hyphens allowed
              </small>
              {domainStatus === 'checking' && (
                <small className="text-info d-flex align-items-center gap-1 mt-1">
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Checking availability...
                </small>
              )}
              {domainStatus === 'available' && (
                <small className="text-success d-flex align-items-center gap-1 mt-1">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Domain is available
                </small>
              )}
              {domainStatus === 'taken' && (
                <small className="text-danger d-flex align-items-center gap-1 mt-1">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Domain is already taken
                </small>
              )}
            </div>

            {/* Location */}
            <div className="col-md-6 mb-3">
              <CustomSelect
                name="country_id"
                label="Country"
                options={countryOptions}
                value={countryOptions.find(
                  (option) => option.value === formik.values.country_id
                )}
                onChange={(selectedOption) => {
                  formik.setFieldValue(
                    "country_id",
                    selectedOption?.value || ""
                  );
                  formik.setFieldValue("city_id", ""); // Reset city when country changes
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.country_id && formik.errors.country_id}
                errorMessage={formik.errors.country_id}
                isLoading={isLoadingCountries}
                required
                placeholder="Select country"
                styles={customStyles}
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomSelect
                name="city_id"
                label="City"
                options={cityOptions}
                value={cityOptions.find(
                  (option) => option.value === formik.values.city_id
                )}
                onChange={(selectedOption) => {
                  formik.setFieldValue("city_id", selectedOption?.value || "");
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.city_id && formik.errors.city_id}
                errorMessage={formik.errors.city_id}
                isLoading={isLoadingCities}
                required
                placeholder="Select city"
                styles={customStyles}
                isDisabled={!formik.values.country_id}
              />
            </div>

            <div className="col-12 mb-3">
              <Input
                label="Address"
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && formik.errors.address}
                errorMessage={formik.errors.address}
                required
                placeholder="Enter full address"
              />
            </div>

            <div className="col-12 mb-4">
              <ThemeBrandingEditor
                primaryColor={formik.values.primary_color}
                secondaryColor={formik.values.secondary_color}
                onPrimaryChange={(color) => formik.setFieldValue("primary_color", color)}
                onSecondaryChange={(color) => formik.setFieldValue("secondary_color", color)}
                onResetDefaults={() => {
                  formik.setFieldValue("primary_color", DEFAULT_THEME.primary);
                  formik.setFieldValue("secondary_color", DEFAULT_THEME.secondary);
                }}
                logoUrl={logoUrl || logoPreview}
                companyName={formik.values.name || "Cooperative Name"}
              />
            </div>

            {/* Subscription Plan */}
            <div className="col-md-6 mb-3">
              <CustomSelect
                name="subscription_id"
                label="Subscription Plan"
                options={subscriptionOptions}
                value={subscriptionOptions.find(
                  (option) => option.value === formik.values.subscription_id
                )}
                onChange={handleSubscriptionChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.subscription_id &&
                  formik.errors.subscription_id
                }
                errorMessage={formik.errors.subscription_id}
                isLoading={isLoadingSubscriptions}
                required
                placeholder="Select subscription plan"
                styles={customStyles}
              />
            </div>

            <div className="col-md-3 mb-3">
              <Input
                label="Number of member slots"
                name="member_slot_count"
                type="number"
                min={1}
                value={formik.values.member_slot_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.member_slot_count &&
                  formik.errors.member_slot_count
                }
                errorMessage={formik.errors.member_slot_count}
                required
                placeholder="e.g. 50"
              />
            </div>

            <div className="col-md-3 mb-3">
              <Input
                label="Discount (%)"
                name="discount_percentage"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={formik.values.discount_percentage}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.discount_percentage &&
                  formik.errors.discount_percentage
                }
                errorMessage={formik.errors.discount_percentage}
                placeholder="0-100"
              />
            </div>

            <div className="col-md-6 mb-3">
              <Input
                label="Subscription Start Date"
                name="subscription_start_date"
                type="date"
                value={formik.values.subscription_start_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.subscription_start_date &&
                  formik.errors.subscription_start_date
                }
                errorMessage={formik.errors.subscription_start_date}
                required
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div className="col-md-4 mb-3">
              <Input
                label="Duration"
                name="subscription_duration"
                type="number"
                value={formik.values.subscription_duration}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.subscription_duration &&
                  formik.errors.subscription_duration
                }
                errorMessage={formik.errors.subscription_duration}
                required
                min={1}
                placeholder="e.g., 1, 2, 12"
              />
            </div>

            <div className="col-md-4 mb-3">
              <CustomSelect
                name="subscription_duration_type"
                label="Duration Type"
                options={[
                  { value: "DAYS", label: "Days" },
                  { value: "WEEKS", label: "Weeks" },
                  { value: "MONTHS", label: "Months" },
                  { value: "YEARS", label: "Years" },
                ]}
                value={[
                  { value: "DAYS", label: "Days" },
                  { value: "WEEKS", label: "Weeks" },
                  { value: "MONTHS", label: "Months" },
                  { value: "YEARS", label: "Years" },
                ].find(
                  (option) => option.value === formik.values.subscription_duration_type
                )}
                onChange={(selectedOption) => {
                  formik.setFieldValue("subscription_duration_type", selectedOption?.value || "MONTHS");
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.subscription_duration_type &&
                  formik.errors.subscription_duration_type
                }
                errorMessage={formik.errors.subscription_duration_type}
                required
                placeholder="Select duration type"
                styles={customStyles}
              />
            </div>

            {/* Display calculated expiry date */}
            {formik.values.subscription_start_date && 
             formik.values.subscription_duration && 
             formik.values.subscription_duration_type && (
              <div className="col-md-4 mb-3 d-flex align-items-end">
                <div className="w-100">
                  <label className="form-label text-muted small">Calculated Expiry Date</label>
                  <div className="p-2 bg-light rounded border">
                    {(() => {
                      const startDate = new Date(formik.values.subscription_start_date);
                      const duration = parseInt(formik.values.subscription_duration);
                      const durationType = formik.values.subscription_duration_type;
                      
                      let expiryDate = new Date(startDate);
                      
                      switch(durationType) {
                        case "DAYS":
                          expiryDate.setDate(expiryDate.getDate() + duration);
                          break;
                        case "WEEKS":
                          expiryDate.setDate(expiryDate.getDate() + (duration * 7));
                          break;
                        case "MONTHS":
                          expiryDate.setMonth(expiryDate.getMonth() + duration);
                          break;
                        case "YEARS":
                          expiryDate.setFullYear(expiryDate.getFullYear() + duration);
                          break;
                      }
                      
                      return expiryDate.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      });
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Details */}
            {selectedSubscription && (
              <div className="col-12 mb-4">
                <div className="card border-[#8B313A] shadow-sm">
                  <div className="card-header bg-[#8B313A] text-white">
                    <h6 className="mb-0 font-semibold">Subscription Details</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p>
                          <strong>Plan:</strong>{" "}
                          {selectedSubscription.name || "N/A"}
                        </p>
                        <p>
                          <strong>Price per member:</strong> ₦
                          {selectedSubscription.price
                            ? parseFloat(
                                selectedSubscription.price
                              ).toLocaleString()
                            : "N/A"}
                        </p>
                        {formik.values.member_slot_count >= 1 && selectedSubscription.price && (
                          <>
                            <p>
                              <strong>Total before discount:</strong> ₦
                              {(
                                formik.values.member_slot_count *
                                parseFloat(selectedSubscription.price)
                              ).toLocaleString()}
                            </p>
                            {formik.values.discount_percentage !== "" &&
                              parseFloat(formik.values.discount_percentage) > 0 && (
                                <p>
                                  <strong>Discount:</strong>{" "}
                                  {parseFloat(formik.values.discount_percentage)}%
                                </p>
                              )}
                            <p>
                              <strong>Total after discount:</strong> ₦
                              {(
                                (formik.values.member_slot_count *
                                  parseFloat(selectedSubscription.price)) *
                                (1 -
                                  (parseFloat(formik.values.discount_percentage) || 0) /
                                    100)
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </p>
                          </>
                        )}
                        <p>
                          <strong>Member Signup Price:</strong> ₦
                          {selectedSubscription.memberSignupPrice
                            ? parseFloat(
                                selectedSubscription.memberSignupPrice
                              ).toLocaleString()
                            : "N/A"}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p>
                          <strong>SMS Units:</strong>{" "}
                          {selectedSubscription.smsUnits || "N/A"}
                        </p>
                        <p>
                          <strong>Email Count:</strong>{" "}
                          {selectedSubscription.emailCount
                            ? parseInt(
                                selectedSubscription.emailCount
                              ).toLocaleString()
                            : "N/A"}
                        </p>
                        <p>
                          <strong>Support Service:</strong>{" "}
                          {selectedSubscription.supportService || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <h6>Included Modules:</h6>
                      <div className="d-flex flex-wrap gap-1">
                        {Array.isArray(selectedSubscription.modules) &&
                        selectedSubscription.modules.length > 0 ? (
                          <>
                            {selectedSubscription.modules
                              .slice(0, 10)
                              .map((module, index) => (
                                <span
                                  key={index}
                                  className="badge bg-secondary me-1 mb-1"
                                >
                                  {module.name}
                                </span>
                              ))}
                            {selectedSubscription.modules.length > 10 && (
                              <span className="badge bg-info">
                                +{selectedSubscription.modules.length - 10} more
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">
                            No modules available
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="d-flex justify-content-between align-items-center mt-6 pt-4 border-top">
            <div className="text-muted small">
              {isUploading && "Please wait while your logo uploads..."}
              {!isUploading &&
                !formik.isValid &&
                formik.dirty &&
                "Please fill in all required fields"}
              {!isUploading &&
                !formik.dirty &&
                "Fill in the form to create a cooperative"}
            </div>
            <div className="d-flex gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  isCreating || isUploading || !formik.isValid || !formik.dirty
                }
                className="px-6 py-3 bg-[#8B313A] hover:bg-[#6B252A] text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isCreating ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating...
                  </>
                ) : isUploading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Create Cooperative
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCooperativePage;
